import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Divider, IconButton,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { itemsApi, purchaseRequisitionsApi, type ItemDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface DraftLine { key: string; item: ItemDto | null; qty: number; reason: string }
const blank = (): DraftLine => ({ key: crypto.randomUUID(), item: null, qty: 0, reason: "" });

/**
 * UC-M1-PROC-01 — Department raises a purchase requisition. The header has only
 * needed-by + reason; warehouse/vendor are chosen at Convert-to-PO time.
 */
export function CreatePurchaseRequisitionPage() {
  const navigate = useNavigate();
  const [neededByDate, setNeededByDate] = useState("");
  const [reason, setReason] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);
  const [attempted, setAttempted] = useState(false);

  const items = useQuery({
    queryKey: ["items", "picker", search],
    queryFn: () => itemsApi.list({ search: search || undefined, pageSize: 50 }),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const dateMissing = !neededByDate;
  const valid = !dateMissing && lines.every(l => l.item && l.qty > 0);
  const submit = () => { setAttempted(true); if (valid) mutation.mutate(); };

  const mutation = useMutation({
    meta: { successMessage: "Purchase requisition created" },
    mutationFn: () => purchaseRequisitionsApi.create({
      neededByDate,
      reason: reason || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, qty: l.qty, reason: l.reason || undefined,
      })),
    }),
    onSuccess: (r) => navigate(`/procurement/purchase-requisitions/${r.id}`),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New purchase indent</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField type="date" label="Needed by *" required size="small" InputLabelProps={{ shrink: true }}
            value={neededByDate} onChange={(e) => setNeededByDate(e.target.value)}
            error={attempted && dateMissing}
            helperText={attempted && dateMissing ? "Needed-by date is required" : undefined}
            sx={{ minWidth: 200 }} />
        </Stack>
        <TextField label="Reason" fullWidth multiline minRows={2}
          value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])}>Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 320 }}>Item *</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>Qty *</TableCell>
            <TableCell>Line reason</TableCell>
            <TableCell sx={{ width: 60 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={items.data?.items ?? []}
                    getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                    value={l.item}
                    onChange={(_, v) => update(l.key, { item: v })}
                    onInputChange={(_, v) => setSearch(v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(p) => <TextField {...p} placeholder={`Line ${idx + 1}`} />}
                    loading={items.isLoading}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 100 }}
                    value={l.qty} onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.reason}
                    onChange={(e) => update(l.key, { reason: e.target.value })} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/procurement/purchase-requisitions")}>Cancel</Button>
          <Button variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={submit}>Save draft indent</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
